import {usePreviewQueryContext} from '@github-ui/saved-views/contexts/PreviewQueryContext'
import {useMemo} from 'react'

import type {BoardInitialPayload} from '../components/board/issues/board-payload'
import {IssuesSavedViewBoard} from '../components/board/issues/IssuesSavedViewBoard'
import {useSavedViewFields} from '../components/saved-view-options/use-saved-view-fields'
import {createRepositoryBoardAdapter} from '../helpers/create-repository-board-adapter'
import styles from './RepositorySavedViewBoard.module.css'

/**
 * A repository saved view's issues as a board, drawn from the board its route already fetched, so
 * mounting this costs no request of its own.
 */
export function RepositorySavedViewBoard({
  board,
  owner,
  repo,
  id,
  query,
  dataUpdatedAt,
  isNavigating,
  basePath,
}: {
  board: BoardInitialPayload
  owner: string
  repo: string
  id: string
  query: string
  /** When the route's response last changed. Included in the key so a new response keys a new board. */
  dataUpdatedAt: number
  /** The route is fetching the response this board is built from. */
  isNavigating: boolean
  /** Where the views' `fields.json` is, which says which custom fields are single-selects. */
  basePath: string
}) {
  // Shared with the view options menu's query.
  const fieldDefinitions = useSavedViewFields(basePath).data?.fields
  const adapter = useMemo(
    () => createRepositoryBoardAdapter({board, owner, repo, id, dataUpdatedAt}),
    [board, owner, repo, id, dataUpdatedAt],
  )
  const previewQuery = usePreviewQueryContext()
  // The repository read and edit views provide this, with the same handler their filter bar uses.
  if (!previewQuery) throw new Error('RepositorySavedViewBoard needs a PreviewQueryProvider')

  return (
    <div className={styles.BoardLayout} data-testid="repository-saved-view-board-layout">
      <IssuesSavedViewBoard
        adapter={adapter}
        query={query}
        isNavigating={isNavigating}
        fieldDefinitions={fieldDefinitions}
        onQueryChange={previewQuery.onQueryChange}
        viewportAnchor="viewport"
        boardClassName={styles.Board}
      />
    </div>
  )
}
